/**
 * GWP-12277: Estimated organization usage-range calculation.
 *
 * Each usage profile carries its own low/high band (wide and asymmetric,
 * straight from the per-meter Confluence content specs). How the band maps to
 * the org total differs by meter:
 *
 *   perOrgUser: true  → low/high are per-org-user/month, multiplied by user count
 *     low  = orgUserCount × band.low
 *     high = orgUserCount × band.high
 *
 *   perOrgUser: false → low/high are absolute monthly totals, used as-is
 *     low  = band.low
 *     high = band.high
 *
 * The result is the low/high pair rendered in the "Your organization needs"
 * ResultsCard on Steps 3 and 5.
 *
 * All values below are from the content specs. Card 1/2/3 in each spec map
 * positionally to exploring/adopting/scaling.
 */

import type { Meter, UsageType } from '../context/types';

interface UsageBand {
	/** Lower bound (per org-user/month, or absolute monthly — see perOrgUser). */
	low: number;
	/** Upper bound (per org-user/month, or absolute monthly — see perOrgUser). */
	high: number;
}

interface MeterUsageModel {
	/**
	 * When true, the band is per org-user/month and multiplied by orgUserCount.
	 * When false, the band is an absolute monthly total independent of org size.
	 */
	perOrgUser: boolean;
	bands: Record<UsageType, UsageBand>;
}

const USAGE_MODEL: Record<Meter, MeterUsageModel> = {
	// Rovo credits: per-user/month × org (Early exploration / Routine adoption / Transformative).
	rovoCredits: {
		perOrgUser: true,
		bands: {
			exploring: { low: 100, high: 500 },
			adopting: { low: 500, high: 2_000 },
			scaling: { low: 2_000, high: 4_000 },
		},
	},
	// Automation steps: per-seat/month × org (Experimenting / Adopting / Scaling).
	automations: {
		perOrgUser: true,
		bands: {
			exploring: { low: 30, high: 200 },
			adopting: { low: 200, high: 850 },
			scaling: { low: 850, high: 3_200 },
		},
	},
	// AI agent resolutions: absolute monthly total, NOT multiplied by org count
	// (Basic / Moderate / Comprehensive).
	csResolutions: {
		perOrgUser: false,
		bands: {
			exploring: { low: 2_000, high: 4_000 },
			adopting: { low: 4_000, high: 6_000 },
			scaling: { low: 6_000, high: 80_000 },
		},
	},
	// Assets objects: per-user × org (Emerging / Adopting / Scaling).
	assetsObjects: {
		perOrgUser: true,
		bands: {
			exploring: { low: 0, high: 3 },
			adopting: { low: 4, high: 10 },
			scaling: { low: 11, high: 50 },
		},
	},
};

export interface UsageRange {
	low: number;
	high: number;
}

export interface EstimateUsageRangeArgs {
	meter: Meter;
	usageType: UsageType;
	orgUserCount: number | undefined;
	/**
	 * Custom usage value from the Needs-step NumericInput. When > 0 it
	 * overrides the usageType estimate entirely (see below). Defaults to 0
	 * (no override).
	 */
	usage?: number;
}

export function estimateUsageRange(args: EstimateUsageRangeArgs): UsageRange {
	const { meter, usageType, orgUserCount, usage } = args;

	// A custom usage value overrides the usageType estimate entirely. It's an
	// absolute org-monthly total, so both bounds collapse to it regardless of
	// meter mode / org size.
	if (usage && usage > 0) {
		return { low: usage, high: usage };
	}

	const model = USAGE_MODEL[meter];
	const band = model.bands[usageType];

	// Absolute meters don't depend on org size — return the band as-is.
	if (!model.perOrgUser) {
		return { low: band.low, high: band.high };
	}

	// Per-user meters need an org size. If none yet, return a zero range and
	// let the UI decide whether to render or hide.
	if (!orgUserCount || orgUserCount <= 0) {
		return { low: 0, high: 0 };
	}

	return {
		low: orgUserCount * band.low,
		high: orgUserCount * band.high,
	};
}
